import { createRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

const ToastsApp = () => {
	const transitionDuration = 250;
	const defaultConfig = {
		autoHide: true,
	};
	const autoHideDelay = 3000 + 2 * transitionDuration;
	const [toasts, setToasts] = useState([]);
	const toastRefs = useRef({});

	const showToast = (message, type = 'info', options = {}) => {
		// Generate a random id
		const id = Math.random().toString(36).substring(7);
		const config = { ...defaultConfig, ...options };

		if (config.autoHide) {
			setTimeout(() => {
				hideToast(id);
			}, autoHideDelay);
		}

		setToasts((prevToasts) => {
			const newToast = {
				id,
				message,
				type, // 'info', 'success', 'error'
				config,
			};

			return [...prevToasts, newToast];
		});
	};

	const hideToast = (id) => {
		setToasts((prevToasts) => {
			return prevToasts.filter((toast) => toast.id !== id);
		});
	};

	useEffect(() => {
		window.showToast = showToast;

		return () => {
			window.showToast = null;
		};
	}, []);

	const getToastRef = (id) => {
		if (!toastRefs.current[id]) {
			toastRefs.current[id] = createRef();
		}
		return toastRefs.current[id];
	};

	const calculateToastHeight = (id) => {
		const toast = toastRefs.current[id]?.current;
		if (toast) {
			toast.style.setProperty('--height', `${toast.offsetHeight}px`);
		}
	};

	return (
		<TransitionGroup component={null}>
			{toasts.map(({ id, message, type, config }) => {
				const nodeRef = getToastRef(id);
				const classNames = ['toast', `toast--${type}`];
				if (config.autoHide) classNames.push('toast--auto-hide');

				return (
					<CSSTransition
						key={id}
						nodeRef={nodeRef}
						classNames="toast"
						timeout={transitionDuration}
						onEntered={() => calculateToastHeight(id)}
						onExited={() => {
							delete toastRefs.current[id];
						}}
					>
						<div
							ref={nodeRef}
							className={classNames.join(' ')}
							style={{
								'--toast-transition-duration': `${transitionDuration}ms`,
								'--toast-auto-hide-delay': `${autoHideDelay}ms`,
							}}
						>
							<div className="toast__header">
								<span className="toast__message">{message}</span>
								<button onClick={() => hideToast(id)}></button>
							</div>
							{config.animationID && (
								<div className="toast__body">
									<div className="toast__animation"></div>
								</div>
							)}
						</div>
					</CSSTransition>
				);
			})}
		</TransitionGroup>
	);
};

const el = document.querySelector('#toast-container');
if (el) {
	const root = ReactDOM.createRoot(el);
	root.render(<ToastsApp />);
}
